import {
  Equipment,
  POWER_GROUP_LABELS,
  PRINTER_MODELS,
  Printer,
  type PrinterList,
  addMonthsIso,
  formatBRL,
  formatIsoDate,
} from '@deizi/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { z } from 'zod';
import { AffixInput, Field } from '../../components/Form';
import { Alert } from '../../components/States';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, decimalInput, parseBrDate, parseCentavos } from '../../lib/numbers';
import { PrinterDialog } from '../printers/PrinterDialog';
import { StepHead, WizardFooter } from './WizardParts';

const x2d = PRINTER_MODELS[0]!;

/** Step 3 · Sua impressora. */
export function StepPrinter({
  printers,
  tariff,
  onBack,
  onNext,
}: {
  printers: z.infer<typeof PrinterList>['data'];
  tariff: number;
  onBack: () => void;
  onNext: () => void;
}) {
  const qc = useQueryClient();
  const existing = printers[0] ?? null;
  const [editing, setEditing] = useState(false);
  const [modelId, setModelId] = useState(x2d.id);
  const model = PRINTER_MODELS.find((m) => m.id === modelId) ?? x2d;
  const [form, setForm] = useState({
    name: 'X2D',
    price: centavosInput(x2d.defaults.purchasePriceCentavos ?? null),
    lifetime: String(x2d.defaults.lifetimeHours ?? 5000),
    low: String(x2d.defaults.powerLowWatts ?? ''),
    high: String(x2d.defaults.powerHighWatts ?? ''),
    // The spec's example plan (10 × R$ 1.249,90 from 17/10/2026), flagged "exemplo: confira".
    count: '10',
    firstDue: '17/10/2026',
  });
  const [idem] = useState(newIdempotencyKey);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const price = parseCentavos(form.price);
  const lifetime = /^\d+$/.test(form.lifetime) ? Number(form.lifetime) : null;
  const low = /^\d+$/.test(form.low) ? Number(form.low) : 0;
  const high = /^\d+$/.test(form.high) ? Number(form.high) : 0;

  const count = /^\d+$/.test(form.count.trim()) ? Number(form.count) : null;
  const firstDue = parseBrDate(form.firstDue);
  const plan =
    count && count > 1 && firstDue && price
      ? {
          count,
          firstDue,
          amount: Math.floor(price / count),
          last: addMonthsIso(firstDue, count - 1),
        }
      : null;
  const create = useMutation({
    mutationFn: async (values: typeof form) => {
      const printer = await api.post(
        '/printers',
        Printer,
        {
          ...model.defaults,
          name: values.name.trim() || model.name,
          model: model.name,
          purchasePriceCentavos: parseCentavos(values.price) ?? 0,
          lifetimeHours: Number(values.lifetime) || 5000,
          powerLowWatts: Number(values.low) || 0,
          powerHighWatts: Number(values.high) || 0,
        },
        { idempotencyKey: idem },
      );
      // "Parcelamento": the printer's equipment gets the installment plan (Despesas).
      const n = Number(values.count);
      const first = parseBrDate(values.firstDue);
      if (n > 1 && first) {
        const list = await api.get('/equipment', z.object({ data: z.array(Equipment) }));
        const eq = list.data.find((e) => e.printer?.id === printer.id);
        if (eq) {
          await api.post(`/equipment/${eq.id}/installments`, Equipment, {
            count: n,
            firstDueOn: first,
          });
        }
      }
      return printer;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['printers'] });
      void qc.invalidateQueries({ queryKey: ['setup'] });
      onNext();
    },
  });

  if (existing) {
    return (
      <>
        <StepHead title="Sua impressora">
          Já cadastrada. Confira os números do custo por hora.
        </StepHead>
        <section className="card col" style={{ gap: 10 }}>
          <div className="row between wrap">
            <h2>{existing.name}</h2>
            <button type="button" className="btn sm" onClick={() => setEditing(true)}>
              Editar
            </button>
          </div>
          <p className="sm muted">{existing.model}</p>
          <div className="kv">
            <span className="muted">Custo por hora · {POWER_GROUP_LABELS.low}</span>
            <span className="v">{formatBRL(existing.costPerHour.low.total)}</span>
          </div>
          <div className="kv">
            <span className="muted">Custo por hora · {POWER_GROUP_LABELS.high}</span>
            <span className="v">{formatBRL(existing.costPerHour.high.total)}</span>
          </div>
        </section>
        <WizardFooter onBack={onBack} onContinue={onNext} />
        {editing ? (
          <PrinterDialog
            printer={existing}
            onClose={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              void qc.invalidateQueries({ queryKey: ['printers'] });
            }}
          />
        ) : null}
      </>
    );
  }

  const dep = price !== null && lifetime ? price / lifetime : 0;
  const energy = (low / 1000) * tariff * 100;
  const maint = model.defaults.maintenancePerHourCentavos ?? 10;
  const total = dep + energy + maint;
  const caps: [string, string | null | undefined][] = [
    ['Bicos', model.defaults.nozzles],
    ['Volume', model.defaults.buildVolume],
    [
      'Multicolor',
      model.defaults.multicolorUnit
        ? `${model.defaults.multicolorUnit} · ${model.defaults.multicolorSlots ?? 0} slots`
        : null,
    ],
    ['Câmara', model.defaults.enclosure],
  ];

  return (
    <>
      <StepHead title="Sua impressora">
        Escolha o modelo e os dados técnicos vêm preenchidos. Só confira o preço.
      </StepHead>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
        <div className="col" style={{ flex: '3 1 380px', minWidth: 0, gap: 14 }}>
          <Field
            id="w-model"
            label="Modelo"
            help={
              model.complete
                ? 'Modelo conhecido: dados completos.'
                : 'Modelo personalizado: confira os dados na ficha técnica.'
            }
          >
            <select
              id="w-model"
              className="input"
              value={modelId}
              onChange={(e) => {
                const m = PRINTER_MODELS.find((x) => x.id === e.target.value) ?? x2d;
                setModelId(m.id);
                setForm({
                  count: '',
                  firstDue: '',
                  name: m.name.split(' ').at(-1) ?? m.name,
                  price: centavosInput(m.defaults.purchasePriceCentavos ?? null),
                  lifetime: String(m.defaults.lifetimeHours ?? 5000),
                  low:
                    m.defaults.powerLowWatts !== undefined ? String(m.defaults.powerLowWatts) : '',
                  high:
                    m.defaults.powerHighWatts !== undefined
                      ? String(m.defaults.powerHighWatts)
                      : '',
                });
              }}
            >
              {PRINTER_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(170px,100%),1fr))',
              gap: 12,
            }}
          >
            <Field id="w-pname" label="Apelido">
              <input
                id="w-pname"
                className="input"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </Field>
            <Field id="w-pprice" label="Preço de compra">
              <AffixInput
                id="w-pprice"
                prefix="R$"
                value={form.price}
                onChange={(v) => set('price', v)}
                invalid={price === null}
              />
            </Field>
            <Field
              id="w-pinst"
              label="Parcelamento"
              help={
                plan
                  ? `${plan.count} × ${formatBRL(plan.amount)} · 1ª em ${formatIsoDate(plan.firstDue)}, última em ${formatIsoDate(plan.last)} (exemplo: confira)`
                  : 'Vazio ou 1 = à vista.'
              }
            >
              <div className="row" style={{ gap: 8 }}>
                <AffixInput
                  id="w-pinst"
                  unit="×"
                  inputMode="numeric"
                  value={form.count}
                  onChange={(v) => set('count', v)}
                  invalid={form.count.trim() !== '' && count === null}
                />
                <input
                  className={form.firstDue && !firstDue ? 'input tab err' : 'input tab'}
                  aria-label="Primeira parcela"
                  placeholder="1ª parcela"
                  inputMode="numeric"
                  value={form.firstDue}
                  onChange={(e) => set('firstDue', e.target.value)}
                />
              </div>
            </Field>
            <Field
              id="w-plife"
              label="Vida útil esperada"
              help="Chute inicial. Troca pela média real quando uma impressora se aposenta."
            >
              <AffixInput
                id="w-plife"
                unit="horas"
                inputMode="numeric"
                value={form.lifetime}
                onChange={(v) => set('lifetime', v)}
                invalid={lifetime === null}
              />
            </Field>
          </div>
          <span className="lbl">Consumo por material</span>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(min(170px,100%),1fr))',
              gap: 12,
            }}
          >
            <Field
              id="w-plow"
              label={POWER_GROUP_LABELS.low}
              help={`Energia por hora: R$ ${decimalInput((low / 1000) * tariff, 2, 2)}`}
            >
              <AffixInput
                id="w-plow"
                unit="W"
                inputMode="numeric"
                value={form.low}
                onChange={(v) => set('low', v)}
              />
            </Field>
            <Field
              id="w-phigh"
              label={POWER_GROUP_LABELS.high}
              help={`Energia por hora: R$ ${decimalInput((high / 1000) * tariff, 2, 2)}`}
            >
              <AffixInput
                id="w-phigh"
                unit="W"
                inputMode="numeric"
                value={form.high}
                onChange={(v) => set('high', v)}
              />
            </Field>
          </div>
          <p className="xs muted">
            Dica: meça com uma tomada inteligente durante algumas impressões. Tarifa usada: R${' '}
            {decimalInput(tariff, 5, 2)}/kWh (passo 2).
          </p>
          <section className="card sunk col" style={{ gap: 4, padding: 14 }}>
            <h3>Capacidades</h3>
            {caps.map(([k, v]) => (
              <div key={k} className="kv">
                <span className="muted">{k}</span>
                <span className="v" style={{ whiteSpace: 'normal', textAlign: 'right' }}>
                  {v ?? 'conferir na ficha técnica'}
                </span>
              </div>
            ))}
          </section>
        </div>
        <aside
          className="card col"
          style={{ flex: '2 1 240px', gap: 10, position: 'sticky', top: 16 }}
        >
          <h3>Custo por hora de máquina</h3>
          <p className="xs muted">imprimindo PLA</p>
          <p className="big">{formatBRL(Math.round(total))}</p>
          <div className="stack" aria-hidden="true">
            <span style={{ flex: dep || 1, background: 'var(--pe)' }} />
            <span style={{ flex: energy || 0.01, background: 'var(--co)' }} />
            <span style={{ flex: maint || 0.01, background: '#7FB2BA' }} />
          </div>
          <div className="kv">
            <span>
              <i className="sw" style={{ background: 'var(--pe)', marginRight: 8 }} />
              Depreciação
            </span>
            <span className="v">{formatBRL(Math.round(dep))}</span>
          </div>
          <div className="kv">
            <span>
              <i className="sw" style={{ background: 'var(--co)', marginRight: 8 }} />
              Energia
            </span>
            <span className="v">{formatBRL(Math.round(energy))}</span>
          </div>
          <div className="kv">
            <span>
              <i className="sw" style={{ background: '#7FB2BA', marginRight: 8 }} />
              Manutenção (estimada)
            </span>
            <span className="v">{formatBRL(maint)}</span>
          </div>
          {energy > 0 ? (
            <p className="xs muted">
              A depreciação é cerca de {Math.round(dep / energy)}× a energia: a vida útil pesa muito
              mais no preço do que a tarifa.
            </p>
          ) : null}
        </aside>
      </div>
      {create.isError ? <Alert tone="err">{errorMessage(create.error)}</Alert> : null}
      <WizardFooter
        busy={create.isPending}
        blockMsg={
          price === null || !lifetime
            ? 'Informe o preço de compra e a vida útil para calcular o custo por hora.'
            : null
        }
        onBack={onBack}
        onSuggest={() => {
          setModelId(x2d.id);
          create.mutate({
            name: 'X2D',
            price: centavosInput(x2d.defaults.purchasePriceCentavos ?? null),
            lifetime: '5000',
            low: '250',
            high: '550',
            count: '10',
            firstDue: '17/10/2026',
          });
        }}
        onContinue={() => create.mutate(form)}
      />
    </>
  );
}
